import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonIcon from "@mui/icons-material/Person";
import ReplayIcon from "@mui/icons-material/Replay";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { LeadContact } from "../types/lead-contacts.types";

interface ContactCardProps {
  contact: LeadContact;
  isSelected: boolean;
  isCopied: boolean;
  onSelect: (leadId: number) => void;
  onCopyContact: (contact: LeadContact) => void;
  onCopyPhone: (phone: string) => void;
  onCopyLeadId: (leadId: number) => void;
  onShowDetails: (contact: LeadContact) => void;
  searchText?: string;
  highlightText?: (text: string, searchTerm: string) => React.ReactNode;
}

const ContactCard: React.FC<ContactCardProps> = ({
  contact,
  isSelected,
  isCopied,
  onSelect,
  onCopyContact,
  onCopyPhone,
  onCopyLeadId,
  onShowDetails,
  searchText = "",
  highlightText,
}) => {
  const getDisplayName = (c: LeadContact) =>
    (c.valid_name || c.name || "").trim();

  const getInitials = (name: string) => {
    const parts = name.split(" ").filter(Boolean);
    const firstTwo = parts.slice(0, 2);
    const initials = firstTwo.map((p) => p.charAt(0).toUpperCase()).join("");
    return initials || "?";
  };

  return (
    <Box
      data-contact-id={contact.id}
      onClick={() => onSelect(contact.id)}
      sx={{
        width: "100%",
        border: 1,
        borderColor: isSelected ? "#2196f3" : isCopied ? "#4caf50" : "#e0e0e0",
        borderRadius: 2,
        p: 1,
        mb: 0.75,
        backgroundColor: isSelected
          ? "#f0f7ff"
          : isCopied
            ? "#f0f8f0"
            : "#fafbfc",
        transition: "all 0.2s ease",
        cursor: "pointer",
        position: "relative",
        "&:hover": {
          backgroundColor: "#f0f7ff",
          borderColor: "#2196f3",
          transform: "translateY(-1px)",
          boxShadow: "0 4px 12px rgba(33, 150, 243, 0.15)",
        },
      }}
    >
      {/* Barra lateral de destaque */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "4px",
          borderTopLeftRadius: "8px",
          borderBottomLeftRadius: "8px",
          backgroundColor: isSelected
            ? "#2196f3"
            : isCopied
              ? "#4caf50"
              : "#e0e0e0",
        }}
      />

      {/* Topo: status + chips à esquerda e COPIADO + ação à direita. Em leads qualidade usa store_status. */}
      {(() => {
        const displayStatus = contact.is_quality_lead
          ? (contact.store_status ?? contact.status)
          : contact.status;
        const statusBg = (() => {
          switch (displayStatus?.toUpperCase()) {
            case "AGENDAMENTO":
              return "#ff9800";
            case "INTERESSADO":
              return "#4caf50";
            case "SEM INTERESSE":
              return "#f57c00";
            case "SOLICITA EXCLUSÃO DO CADASTRO":
              return "#c62828";
            default:
              return "#9e9e9e";
          }
        })();
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.75,
              pt: contact.lead_id || contact.last_time_updated ? 1.75 : 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "4px 8px",
                  borderRadius: "8px",
                  fontSize: "10px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  backgroundColor: statusBg,
                  color: "white",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
              >
                {displayStatus || contact.status}
              </Box>
              {contact.fixed && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "4px 8px",
                    borderRadius: "8px",
                    fontSize: "10px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    backgroundColor: "#e91e63",
                    color: "white",
                    marginLeft: "6px",
                    boxShadow: "0 2px 4px rgba(233, 30, 99, 0.2)",
                  }}
                >
                  LEAD ALTERADO
                </Box>
              )}
            </Box>

            {/* Lado direito: Ícone COPIADO + Botão de detalhes + Botão de copiar */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isCopied && (
                <Tooltip title="Lead já copiado">
                  <CheckCircleIcon
                    sx={{
                      color: "#4caf50",
                      fontSize: "20px",
                      filter: "drop-shadow(0 1px 2px rgba(76, 175, 80, 0.3))",
                    }}
                  />
                </Tooltip>
              )}
              <Tooltip title="Ver detalhes completos do lead">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowDetails(contact);
                  }}
                  aria-label="Ver detalhes do lead"
                  sx={{
                    color: "#1976d2",
                    borderRadius: "8px",
                    backgroundColor: "rgba(25, 118, 210, 0.05)",
                    border: "1px solid rgba(25, 118, 210, 0.2)",
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.1)",
                      borderColor: "rgba(25, 118, 210, 0.3)",
                    },
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copiar informações do contato">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCopyContact(contact);
                  }}
                  aria-label="Copiar informações do contato"
                  sx={{
                    color: "#008069",
                    borderRadius: "8px",
                    backgroundColor: "rgba(0, 128, 105, 0.05)",
                    border: "1px solid rgba(0, 128, 105, 0.2)",
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "rgba(0, 128, 105, 0.1)",
                      borderColor: "rgba(0, 128, 105, 0.3)",
                    },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        );
      })()}

      {/* Linha: checkbox + avatar + nome + telefone e ID */}
      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          alignItems: "center",
          position: "relative",
        }}
      >
        <Checkbox
          checked={isSelected}
          onChange={() => onSelect(contact.id)}
          onClick={(e) => {
            e.stopPropagation();
          }}
          sx={{
            padding: "2px",
            "&.Mui-checked": {
              color: "#008069",
            },
          }}
        />
        <Avatar
          sx={{ width: 32, height: 32, bgcolor: "#008069", flexShrink: 0 }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
            {getInitials(getDisplayName(contact))}
          </Typography>
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Linha 1: Nome */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
            <Tooltip
              title={(getDisplayName(contact) || "")
                .split(" ")
                .map(
                  (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
                )
                .join(" ")}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "#111b21",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {highlightText && searchText
                  ? highlightText(
                      (getDisplayName(contact) || "")
                        .split(" ")
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() +
                            word.slice(1).toLowerCase(),
                        )
                        .join(" "),
                      searchText,
                    )
                  : (getDisplayName(contact) || "")
                      .split(" ")
                      .map(
                        (word) =>
                          word.charAt(0).toUpperCase() +
                          word.slice(1).toLowerCase(),
                      )
                      .join(" ")}
              </Typography>
            </Tooltip>
            {contact.is_from_recap && (
              <Tooltip title="Lead de repescagem">
                <Chip
                  icon={<ReplayIcon sx={{ fontSize: "12px !important" }} />}
                  label="Repescagem"
                  size="small"
                  sx={{
                    height: "18px",
                    fontSize: "9px",
                    fontWeight: 600,
                    backgroundColor: "#fff3e0",
                    color: "#e65100",
                    border: "1px solid #ffb74d",
                    "& .MuiChip-icon": {
                      marginLeft: "4px",
                      marginRight: "-4px",
                    },
                    "& .MuiChip-label": {
                      paddingLeft: "4px",
                      paddingRight: "6px",
                    },
                  }}
                />
              </Tooltip>
            )}
            {contact.is_quality_lead && (
              <Tooltip title="Lead de Controle de Qualidade">
                <Chip
                  label="Qualidade"
                  size="small"
                  sx={{
                    height: "18px",
                    fontSize: "9px",
                    fontWeight: 600,
                    backgroundColor: "#e8eaf6",
                    color: "#3949ab",
                    border: "1px solid #9fa8da",
                    "& .MuiChip-label": {
                      paddingLeft: "6px",
                      paddingRight: "6px",
                    },
                  }}
                />
              </Tooltip>
            )}
          </Box>

          {/* Linha 2: Telefone */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title={contact.valid_telephone || contact.telephone}>
              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontSize: "12px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {highlightText && searchText
                  ? highlightText(
                      contact.valid_telephone || contact.telephone,
                      searchText,
                    )
                  : contact.valid_telephone || contact.telephone}
              </Typography>
            </Tooltip>
            <Tooltip title="Copiar telefone">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onCopyPhone(contact.valid_telephone || contact.telephone);
                }}
                sx={{ color: "#1976d2", padding: "2px", flexShrink: 0 }}
              >
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 0.75 }} />

      {/* Informações de Atendente e Coordenador */}
      {(contact.attendant_name || contact.coordinator_name) && (
        <Box sx={{ mt: 0.25 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {contact.attendant_name && (
              <Tooltip title={contact.attendant_name}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 0.75,
                    py: 0.35,
                    borderRadius: "12px",
                    backgroundColor: "#e8f5e9",
                    border: "1px solid #c8e6c9",
                    maxWidth: "47%",
                    transition: "all 0.2s ease",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                    "&:hover": {
                      backgroundColor: "#c8e6c9",
                      transform: "translateY(-1px)",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  <PersonIcon sx={{ fontSize: "13px", color: "#4caf50" }} />
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "#2e7d32",
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {contact.attendant_name}
                  </Typography>
                </Box>
              </Tooltip>
            )}
            {contact.coordinator_name && (
              <Tooltip title={contact.coordinator_name}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 0.75,
                    py: 0.35,
                    borderRadius: "12px",
                    backgroundColor: "#e3f2fd",
                    border: "1px solid #bbdefb",
                    maxWidth: "47%",
                    transition: "all 0.2s ease",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                    "&:hover": {
                      backgroundColor: "#bbdefb",
                      transform: "translateY(-1px)",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  <SupervisorAccountIcon
                    sx={{ fontSize: "13px", color: "#1976d2" }}
                  />
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "#1565c0",
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {contact.coordinator_name}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}

      {/* ID posicionado no canto superior esquerdo do card */}
      {contact.lead_id && (
        <Box
          sx={{
            position: "absolute",
            left: 12,
            top: 8,
            zIndex: 1,
          }}
        >
          <Tooltip title={`Copiar ID do lead: ${contact.lead_id}`}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                onCopyLeadId(contact.lead_id);
              }}
            >
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#8696a0",
                  userSelect: "none",
                }}
              >
                🏷️
              </Typography>
              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontSize: "11px",
                  color: "#8696a0",
                  userSelect: "none",
                }}
              >
                {contact.lead_id}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      )}

      {/* Data posicionada no canto superior direito do card, como nos balões de mensagem */}
      {contact.last_time_updated && (
        <Box
          sx={{
            position: "absolute",
            right: 12,
            top: 8,
            zIndex: 1,
          }}
        >
          <Tooltip
            title={(() => {
              try {
                const date = new Date(contact.last_time_updated);
                if (isNaN(date.getTime())) return "";
                return date.toLocaleString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                });
              } catch {
                return "";
              }
            })()}
          >
            <Typography
              sx={{
                color: "#8696a0",
                fontSize: "11px",
                lineHeight: "1",
                userSelect: "none",
              }}
            >
              {(() => {
                try {
                  const date = new Date(contact.last_time_updated);
                  if (isNaN(date.getTime())) return "";
                  return date.toLocaleString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                } catch {
                  return "";
                }
              })()}
            </Typography>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default ContactCard;
